import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {
  Calendar,
  Save,
  Loader2,
  ChevronLeft,
  ChevronRight,
  X,
  Info,
  CalendarX,
  CalendarCheck,
  Settings,
  Clock,
  Plus,
  Trash2,
  Globe,
  Copy,
  CheckSquare,
  Check,
  DoorOpen,
} from "lucide-react";
import { useLocation } from "../contexts/LocationContext";
import { getAuthToken } from "../lib/api-client";
import { useGlobalToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { usePermissions } from "../hooks/usePermissions";
import TimezoneSelect from "../components/TimezoneSelect";

interface Shift {
  name: string;
  startTime: string;
  endTime: string;
  enabled: boolean;
}

interface ScheduleConfig {
  id: string;
  defaultDuration: number;
  slotInterval: number;
  maxAdvanceDays: number;
  minAdvanceHours: number;
  maxPartySize: number;
  weeklySchedule: Record<string, boolean>;
  shifts: Shift[];
  tableReleaseFrom: string;
  // Si es `false`, la location funciona en modo aforo (sin asignación de
  // mesa): los salones no limitan las reservas online, solo las agrupan
  // visualmente en el widget. Ver `LocationSettings.tsx` para el toggle.
  useTableAssignment?: boolean;
}

interface ScheduleOverride {
  id: string;
  date: string;
  isClosed: boolean;
  reason: string | null;
  customShifts: Shift[] | null;
  maxReservations: number | null;
  disabledRoomIds: string[];
}

// Salón de la location (mismo concepto que `Room` en TableMap.tsx/Reservations.tsx,
// aquí solo se necesita id+nombre para el toggle de habilitado/deshabilitado por día).
interface RoomOption {
  id: string;
  name: string;
}

const DAY_NAMES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const FULL_DAY_NAMES = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];

const JS_TO_VISUAL_DAY: Record<number, number> = {
  0: 6,
  1: 0,
  2: 1,
  3: 2,
  4: 3,
  5: 4,
  6: 5,
};

const VISUAL_TO_JS_DAY = [1, 2, 3, 4, 5, 6, 0];

interface OpeningHourRange {
  open: string;
  close: string;
}
interface OpeningHourEntry {
  ranges: OpeningHourRange[];
}
type OpeningHours = Record<string, OpeningHourEntry>;

function normalizeOpeningHours(raw: unknown): OpeningHours {
  if (!raw || typeof raw !== "object") return {};
  const result: OpeningHours = {};
  for (const [key, val] of Object.entries(raw as Record<string, unknown>)) {
    if (!val || typeof val !== "object") continue;
    const v = val as Record<string, unknown>;
    if (Array.isArray(v.ranges)) {
      result[key] = { ranges: v.ranges as OpeningHourRange[] };
    } else if (typeof v.open === "string" && typeof v.close === "string") {
      result[key] = { ranges: [{ open: v.open, close: v.close }] };
    }
  }
  return result;
}

const DEFAULT_SHIFTS: Shift[] = [
  { name: "Comida", startTime: "13:00", endTime: "16:00", enabled: true },
  { name: "Cena", startTime: "20:00", endTime: "23:30", enabled: true },
];

const DEFAULT_WEEKLY_SCHEDULE: Record<string, boolean> = {
  "0": false,
  "1": true,
  "2": true,
  "3": true,
  "4": true,
  "5": true,
  "6": true,
};

// Compara dos listas de turnos campo a campo (nombre/inicio/fin/habilitado).
// Se usa tanto para pintar "Horario especial" en el calendario (solo si de
// verdad difiere de los turnos por defecto) como para decidir si un override
// de un día concreto tiene horario personalizado.
function shiftsDiffer(a: Shift[], b: Shift[]): boolean {
  if (a.length !== b.length) return true;
  return a.some((shiftA, i) => {
    const shiftB = b[i];
    if (!shiftB) return true;
    return (
      shiftA.name !== shiftB.name ||
      shiftA.startTime !== shiftB.startTime ||
      shiftA.endTime !== shiftB.endTime ||
      shiftA.enabled !== shiftB.enabled
    );
  });
}

// Valida los turnos HABILITADOS de una lista antes de guardar: hora de fin
// posterior a la de inicio y sin solapamientos entre ellos. El backend
// también valida esto (Zod) y devuelve 400 con mensaje en español, pero
// validar aquí evita el viaje de red y un guardado en silencio que deja el
// turno sin franjas. Las horas son strings "HH:MM" con el mismo formato
// (2 dígitos), así que la comparación lexicográfica equivale a comparar horas.
function validateShifts(shiftsToCheck: Shift[]): string | null {
  const enabledShifts = shiftsToCheck.filter((s) => s.enabled);

  for (const shift of enabledShifts) {
    if (shift.endTime <= shift.startTime) {
      return `El turno "${shift.name}" tiene la hora de fin (${shift.endTime}) igual o anterior a la de inicio (${shift.startTime}). Corrígelo antes de guardar.`;
    }
  }

  for (let i = 0; i < enabledShifts.length; i++) {
    for (let j = i + 1; j < enabledShifts.length; j++) {
      const a = enabledShifts[i];
      const b = enabledShifts[j];
      if (a.startTime < b.endTime && b.startTime < a.endTime) {
        return `Los turnos "${a.name}" (${a.startTime}-${a.endTime}) y "${b.name}" (${b.startTime}-${b.endTime}) se solapan. Ajusta los horarios antes de guardar.`;
      }
    }
  }

  return null;
}

// Extrae el mensaje de error en español que devuelve el backend (ej. validación
// Zod de turnos, o 403 "No tienes permisos..." para staff) de una respuesta no
// exitosa. Si el cuerpo no es JSON o no trae `message`, usa el fallback.
async function parseErrorMessage(
  res: Response,
  fallback: string,
): Promise<string> {
  try {
    const data = await res.json();
    if (data && typeof data.message === "string" && data.message.trim()) {
      return data.message;
    }
  } catch {
    // Cuerpo no-JSON o vacío: usar el mensaje por defecto
  }
  return fallback;
}

export default function Schedule() {
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000/api";
  const navigate = useNavigate();
  const {
    locationVersion,
    currentLocation,
    currentLocationId,
    currentBookingMode,
  } = useLocation();
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();
  const { can } = usePermissions();
  // Solo quien tenga el permiso "horario:editar" (owner/admin siempre lo
  // tienen) puede editar/guardar el horario (el backend también lo restringe
  // y devuelve 403 si no procede). Deshabilitamos aquí los botones de
  // edición/guardado para dar feedback inmediato; el mensaje 403 del backend
  // se sigue mostrando igualmente si algo se cuela.
  const canEditSchedule = can("horario:editar");

  const [config, setConfig] = useState<ScheduleConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);

  const [shifts, setShifts] = useState<Shift[]>(DEFAULT_SHIFTS);
  const [weeklySchedule, setWeeklySchedule] = useState<Record<string, boolean>>(
    DEFAULT_WEEKLY_SCHEDULE,
  );
  const [openingHours, setOpeningHours] = useState<OpeningHours>({});
  const [openingHoursCollapsed, setOpeningHoursCollapsed] = useState(false);
  const [timezone, setTimezone] = useState<string>("Europe/Madrid");
  const [activeTab, setActiveTab] = useState<"calendar" | "general">(
    "calendar",
  );
  const [configSaving, setConfigSaving] = useState(false);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [overrides, setOverrides] = useState<ScheduleOverride[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [overridesLoading, setOverridesLoading] = useState(false);

  // Selección múltiple de días: al activarla, cada clic en el calendario
  // añade/quita el día en vez de abrir el panel de un único día. `selectedDate`
  // y `selectedDates` son mutuamente excluyentes (se limpia el otro al cambiar
  // de modo), así el flujo de un solo día sigue funcionando exactamente igual.
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);

  const [rooms, setRooms] = useState<RoomOption[]>([]);

  const [dayPanelShifts, setDayPanelShifts] = useState<Shift[]>([]);
  const [dayPanelClosed, setDayPanelClosed] = useState(false);
  const [dayPanelReason, setDayPanelReason] = useState("");
  // Salones deshabilitados ese día (feature Room por fecha, p.ej. una feria
  // que solo abre un salón). Compartido entre el panel de un día y el de
  // varios días seleccionados: solo cambia de dónde se inicializa.
  const [dayPanelDisabledRoomIds, setDayPanelDisabledRoomIds] = useState<
    string[]
  >([]);
  const [dayPanelSaving, setDayPanelSaving] = useState(false);
  const [dayPanelHasChanges, setDayPanelHasChanges] = useState(false);

  const getAuthHeaders = () => {
    const locationId = localStorage.getItem("rm_current_location_id");
    const organizationId = localStorage.getItem("rm_current_organization_id");
    const token = getAuthToken();
    return {
      "Content-Type": "application/json",
      ...(locationId ? { "X-Location-Id": locationId } : {}),
      ...(organizationId ? { "X-Organization-Id": organizationId } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const authFetch = (url: string, options: RequestInit = {}) =>
    fetch(url, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...(options.headers as Record<string, string>),
      },
      credentials: "include",
    });

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const jsDay = firstDay.getDay();
    const startOffset = jsDay === 0 ? 6 : jsDay - 1;
    const totalDays = lastDay.getDate();
    const days: { date: Date; isCurrentMonth: boolean }[] = [];

    for (let i = startOffset - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({ date, isCurrentMonth: false });
    }
    for (let i = 1; i <= totalDays; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    return days;
  };

  // Format date as YYYY-MM-DD using local timezone (not UTC)
  const formatDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getOverrideForDate = (dateKey: string) => {
    return overrides.find((o) => o.date.split("T")[0] === dateKey);
  };

  const isDayOpenByDefault = (date: Date) => {
    const dayKey = date.getDay().toString();
    return weeklySchedule[dayKey] !== false;
  };

  const isDayClosed = (date: Date) => {
    const dateKey = formatDateKey(date);
    const override = getOverrideForDate(dateKey);
    if (override) return override.isClosed;
    return !isDayOpenByDefault(date);
  };

  // Solo es "Horario especial" (azul) si el override tiene turnos que
  // realmente DIFIEREN de los turnos por defecto — no basta con que tenga
  // `customShifts`: puede ser un override que solo deshabilita un salón, con
  // los mismos turnos que la configuración general (ver punto morado aparte).
  const hasCustomShifts = (date: Date): boolean => {
    const dateKey = formatDateKey(date);
    const override = getOverrideForDate(dateKey);
    if (!override || override.isClosed || !override.customShifts) {
      return false;
    }
    return shiftsDiffer(override.customShifts, shifts);
  };

  const loadConfig = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/schedule/config`, {});
      if (res.status === 401) {
        navigate("/login");
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setConfig(data.config);
        if (data.config.shifts) setShifts(data.config.shifts);
        if (data.config.weeklySchedule)
          setWeeklySchedule(data.config.weeklySchedule);
        setOpeningHours(normalizeOpeningHours(data.config.openingHours));
        if (currentLocation?.timezone) setTimezone(currentLocation.timezone);
      }
    } catch (error) {
      console.error("Error loading config:", error);
      showToast("No se pudo cargar la configuración de horarios", "error");
    } finally {
      setConfigLoading(false);
    }
  }, [API_URL, navigate]);

  // Reutiliza el mismo endpoint que ya usa el mapa de mesas (TableMap.tsx) y
  // el listado de reservas (Reservations.tsx) para cargar los salones de la
  // location actual. Sin `includeInactive=true`: solo interesan los salones
  // activos, que son los que de verdad se pueden deshabilitar por día.
  const loadRooms = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/tables/rooms`);
      if (res.ok) {
        const data = await res.json();
        const loadedRooms: RoomOption[] = (data.rooms || data.data || []).map(
          (r: { id: string; name: string }) => ({ id: r.id, name: r.name }),
        );
        setRooms(loadedRooms);
      }
    } catch (error) {
      console.error("Error loading rooms:", error);
      showToast("No se pudieron cargar los salones", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL]);

  // Nº de secuencia de la carga de overrides: si hay dos peticiones en vuelo
  // (p. ej. cambio de sede rápido), solo la ÚLTIMA lanzada puede escribir el
  // estado — una respuesta rezagada de la sede anterior ya no pisa el
  // calendario de la actual.
  const overridesSeq = useRef(0);

  const loadOverrides = useCallback(async () => {
    const seq = ++overridesSeq.current;
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    // Use local timezone for date range (not UTC)
    const startDate = formatDateKey(new Date(year, month, 1));
    const endDate = formatDateKey(new Date(year, month + 1, 0));

    setOverridesLoading(true);
    try {
      const res = await authFetch(
        `${API_URL}/schedule/overrides?startDate=${startDate}&endDate=${endDate}`,
      );
      if (res.ok && seq === overridesSeq.current) {
        const data = await res.json();
        if (seq === overridesSeq.current) setOverrides(data.overrides);
      }
    } catch (error) {
      console.error("Error loading overrides:", error);
      if (seq === overridesSeq.current) {
        showToast("No se pudo cargar el calendario de horarios", "error");
      }
    } finally {
      if (seq === overridesSeq.current) setOverridesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL, currentDate]);

  // Recargar config/salones al cambiar de sede. OJO (bug 14-oct): depender
  // del OBJETO currentLocation re-ejecutaba esto en cada re-fetch de la lista
  // de sedes (nueva referencia, MISMA sede) y el setOverrides([]) borraba los
  // días especiales sin recargarlos (el efecto de abajo no se re-disparaba):
  // el día "Cerrado" se veía en rojo un instante y se quedaba en verde.
  // Depender del id (valor) corta la re-ejecución espuria.
  useEffect(() => {
    setOverrides([]); // Clear old data
    loadConfig();
    loadRooms();
  }, [loadConfig, loadRooms, locationVersion, currentLocationId]);

  // Reload overrides when location changes (y al resolverse la sede inicial)
  useEffect(() => {
    loadOverrides();
  }, [loadOverrides, locationVersion, currentLocationId]);

  // Al cambiar de location, cualquier selección de día (single o multi) y el
  // estado del panel pertenecen a la location ANTERIOR — si no se limpian,
  // guardar sobre la nueva location manda `disabledRoomIds` con ids de
  // salones que no le pertenecen (400 genérico del backend: "Uno o más
  // salones seleccionados no existen o no pertenecen a esta ubicación").
  useEffect(() => {
    setMultiSelectMode(false);
    setSelectedDates([]);
    setSelectedDate(null);
    setDayPanelClosed(false);
    setDayPanelReason("");
    setDayPanelShifts([]);
    setDayPanelDisabledRoomIds([]);
    setDayPanelHasChanges(false);
  }, [currentLocationId]);

  useEffect(() => {
    if (multiSelectMode) return;
    if (!selectedDate) return;
    const override = getOverrideForDate(selectedDate);
    if (override) {
      setDayPanelClosed(override.isClosed);
      setDayPanelReason(override.reason || "");
      setDayPanelShifts(override.customShifts || shifts.map((s) => ({ ...s })));
      setDayPanelDisabledRoomIds(override.disabledRoomIds || []);
    } else {
      const date = new Date(selectedDate);
      setDayPanelClosed(!isDayOpenByDefault(date));
      setDayPanelReason("");
      setDayPanelShifts(shifts.map((s) => ({ ...s })));
      setDayPanelDisabledRoomIds([]);
    }
    setDayPanelHasChanges(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, overrides, multiSelectMode]);

  // Al ACTIVAR el modo múltiple, el panel parte siempre de la configuración
  // por defecto (nunca se infiere de los días ya seleccionados, que pueden
  // tener configuraciones distintas entre sí) — la UI dejará claro que al
  // guardar se sobrescribirá lo que hubiera en cada día seleccionado.
  useEffect(() => {
    if (!multiSelectMode) return;
    setDayPanelClosed(false);
    setDayPanelReason("");
    setDayPanelShifts(shifts.map((s) => ({ ...s })));
    setDayPanelDisabledRoomIds([]);
    setDayPanelHasChanges(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiSelectMode]);

  // Construye el body de `POST /schedule/overrides/bulk` con el estado
  // COMPLETO del panel. El backend trata este endpoint como REEMPLAZO TOTAL
  // de los campos gestionados (lo que no se envíe se pone a null/[]/false),
  // así que hay que mandar SIEMPRE `isClosed`/`customShifts`/
  // `disabledRoomIds` — también con el día cerrado, para no perder esa
  // configuración al reabrirlo más adelante. `reason` es la única excepción:
  // si está vacío se omite (el backend lo pone a `null`, que es lo correcto).
  const buildOverridePayload = (dates: string[]) => ({
    dates,
    isClosed: dayPanelClosed,
    customShifts: dayPanelShifts,
    disabledRoomIds: dayPanelDisabledRoomIds,
    ...(dayPanelReason ? { reason: dayPanelReason } : {}),
  });

  const saveDayOverride = async () => {
    if (!selectedDate) return;

    if (!dayPanelClosed) {
      const validationError = validateShifts(dayPanelShifts);
      if (validationError) {
        showToast(validationError, "error");
        return;
      }
    }

    setDayPanelSaving(true);
    try {
      const date = new Date(selectedDate);
      const isDefaultClosed = !isDayOpenByDefault(date);

      // Check if the day panel shifts differ from the default shifts
      const hasCustomSchedule = shiftsDiffer(dayPanelShifts, shifts);

      // Only delete override if returning to complete default state
      const isReturningToDefault =
        dayPanelClosed === isDefaultClosed &&
        !hasCustomSchedule &&
        !dayPanelReason &&
        dayPanelDisabledRoomIds.length === 0;

      if (isReturningToDefault) {
        const res = await authFetch(`${API_URL}/schedule/overrides/bulk`, {
          method: "DELETE",
          body: JSON.stringify({ dates: [selectedDate] }),
        });
        if (!res.ok) {
          const message = await parseErrorMessage(res, "Error al guardar el día");
          throw new Error(message);
        }
      } else {
        // Always save as override if there's any customization
        const res = await authFetch(`${API_URL}/schedule/overrides/bulk`, {
          method: "POST",
          body: JSON.stringify(buildOverridePayload([selectedDate])),
        });
        if (!res.ok) {
          const message = await parseErrorMessage(res, "Error al guardar el día");
          throw new Error(message);
        }
        const data = await res.json().catch(() => null);
        if (Array.isArray(data?.warnings) && data.warnings.length > 0) {
          showToast(data.warnings.join(" "), "info");
        }
      }
      await loadOverrides();
      setDayPanelHasChanges(false);
      showToast("Día actualizado correctamente");
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : "Error al guardar el día",
        "error",
      );
    } finally {
      setDayPanelSaving(false);
    }
  };

  const resetDayToDefault = async () => {
    if (!selectedDate) return;
    const confirmed = await confirm({
      title: "¿Restaurar este día a la configuración por defecto?",
      message:
        "Se perderá el horario, motivo de cierre y los salones deshabilitados personalizados para este día. Pasará a usar la configuración general.",
      confirmText: "Sí, restaurar",
      danger: true,
    });
    if (!confirmed) return;

    setDayPanelSaving(true);
    try {
      const res = await authFetch(`${API_URL}/schedule/overrides/bulk`, {
        method: "DELETE",
        body: JSON.stringify({ dates: [selectedDate] }),
      });
      if (!res.ok) {
        const message = await parseErrorMessage(res, "Error al restaurar el día");
        throw new Error(message);
      }
      await loadOverrides();
      showToast("Día restaurado a configuración por defecto");
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : "Error al restaurar el día",
        "error",
      );
    } finally {
      setDayPanelSaving(false);
    }
  };

  // Guarda la MISMA configuración del panel para todos los días seleccionados
  // en modo múltiple, vía el mismo endpoint bulk (sin la rama "volver a
  // default" del modo de un día: en multi, guardar con el panel en su estado
  // por defecto es una acción válida en sí misma — resetea esos días).
  const saveMultiDayOverride = async () => {
    if (selectedDates.length === 0) return;

    if (!dayPanelClosed) {
      const validationError = validateShifts(dayPanelShifts);
      if (validationError) {
        showToast(validationError, "error");
        return;
      }
    }

    setDayPanelSaving(true);
    try {
      const res = await authFetch(`${API_URL}/schedule/overrides/bulk`, {
        method: "POST",
        body: JSON.stringify(buildOverridePayload(selectedDates)),
      });
      if (!res.ok) {
        const message = await parseErrorMessage(
          res,
          "Error al guardar los días seleccionados",
        );
        throw new Error(message);
      }
      const data = await res.json().catch(() => null);
      if (Array.isArray(data?.warnings) && data.warnings.length > 0) {
        showToast(data.warnings.join(" "), "info");
      }
      await loadOverrides();
      setDayPanelHasChanges(false);
      showToast(
        `${selectedDates.length} día${selectedDates.length !== 1 ? "s" : ""} actualizado${selectedDates.length !== 1 ? "s" : ""} correctamente`,
      );
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : "Error al guardar los días seleccionados",
        "error",
      );
    } finally {
      setDayPanelSaving(false);
    }
  };

  const resetMultiDaysToDefault = async () => {
    if (selectedDates.length === 0) return;
    const confirmed = await confirm({
      title: `¿Restaurar ${selectedDates.length} día${selectedDates.length !== 1 ? "s" : ""} a la configuración por defecto?`,
      message:
        "Se perderá el horario, motivo de cierre y los salones deshabilitados personalizados de todos los días seleccionados. Pasarán a usar la configuración general.",
      confirmText: "Sí, restaurar",
      danger: true,
    });
    if (!confirmed) return;

    setDayPanelSaving(true);
    try {
      const res = await authFetch(`${API_URL}/schedule/overrides/bulk`, {
        method: "DELETE",
        body: JSON.stringify({ dates: selectedDates }),
      });
      if (!res.ok) {
        const message = await parseErrorMessage(
          res,
          "Error al restaurar los días seleccionados",
        );
        throw new Error(message);
      }
      await loadOverrides();
      setDayPanelClosed(false);
      setDayPanelReason("");
      setDayPanelShifts(shifts.map((s) => ({ ...s })));
      setDayPanelDisabledRoomIds([]);
      setDayPanelHasChanges(false);
      showToast(
        `${selectedDates.length} día${selectedDates.length !== 1 ? "s" : ""} restaurado${selectedDates.length !== 1 ? "s" : ""} a configuración por defecto`,
      );
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : "Error al restaurar los días seleccionados",
        "error",
      );
    } finally {
      setDayPanelSaving(false);
    }
  };

  // Alterna un salón deshabilitado/habilitado en el panel del día (o de los
  // días seleccionados en modo múltiple) — mismo estado compartido, ver
  // comentario en la declaración de `dayPanelDisabledRoomIds`.
  const toggleDayPanelRoom = (roomId: string) => {
    setDayPanelDisabledRoomIds((prev) =>
      prev.includes(roomId)
        ? prev.filter((id) => id !== roomId)
        : [...prev, roomId],
    );
    setDayPanelHasChanges(true);
  };

  // Activa/desactiva el modo de selección múltiple de días. Al activarlo se
  // limpia la selección de un solo día; al desactivarlo se limpia la
  // selección múltiple — ambos estados son mutuamente excluyentes.
  const toggleMultiSelectMode = () => {
    setMultiSelectMode((prev) => {
      const next = !prev;
      if (next) {
        setSelectedDate(null);
      } else {
        setSelectedDates([]);
      }
      return next;
    });
  };

  // Añade/quita un día de la selección múltiple al hacer clic en el calendario.
  const toggleDateSelection = (dateKey: string) => {
    setSelectedDates((prev) =>
      prev.includes(dateKey)
        ? prev.filter((d) => d !== dateKey)
        : [...prev, dateKey],
    );
  };

  const updateDayPanelShift = (
    index: number,
    field: keyof Shift,
    value: string | boolean,
  ) => {
    setDayPanelShifts((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    );
    setDayPanelHasChanges(true);
  };

  const saveGeneralConfig = async () => {
    const validationError = validateShifts(shifts);
    if (validationError) {
      showToast(validationError, "error");
      return;
    }

    setConfigSaving(true);
    try {
      const res = await authFetch(`${API_URL}/schedule/config`, {
        method: "PUT",
        body: JSON.stringify({
          shifts,
          weeklySchedule,
          openingHours:
            Object.keys(openingHours).length > 0 ? openingHours : null,
        }),
      });
      if (currentLocationId && timezone) {
        try {
          const tzRes = await authFetch(
            `${API_URL}/locations/${currentLocationId}`,
            {
              method: "PUT",
              body: JSON.stringify({ timezone }),
            },
          );
          if (!tzRes.ok) {
            showToast(
              "No se pudo actualizar la zona horaria (el resto de la configuración sí se guardó)",
              "info",
            );
          }
        } catch {
          showToast(
            "No se pudo actualizar la zona horaria por un error de conexión (el resto de la configuración sí se guardó)",
            "info",
          );
        }
      }
      if (res.ok) {
        const data = await res.json();
        setConfig(data.config);
        if (data.config.shifts) setShifts(data.config.shifts);
        if (data.config.weeklySchedule)
          setWeeklySchedule(data.config.weeklySchedule);
        showToast("Configuración guardada correctamente");
      } else {
        const error = await res.json();
        showToast(error.message || "Error al guardar", "error");
      }
    } catch {
      showToast("Error de conexión", "error");
    } finally {
      setConfigSaving(false);
    }
  };

  const toggleDefaultDay = (visualIdx: number) => {
    const jsDay = VISUAL_TO_JS_DAY[visualIdx];
    const dayKey = jsDay.toString();
    setWeeklySchedule((prev) => ({ ...prev, [dayKey]: !prev[dayKey] }));
  };

  const updateShift = (
    index: number,
    field: keyof Shift,
    value: string | boolean,
  ) => {
    setShifts((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    );
  };

  const addShift = () => {
    setShifts((prev) => [
      ...prev,
      {
        name: "Nuevo turno",
        startTime: "12:00",
        endTime: "15:00",
        enabled: true,
      },
    ]);
  };

  const removeShift = async (index: number) => {
    const shift = shifts[index];
    const confirmed = await confirm({
      title: `¿Eliminar el turno "${shift?.name || "sin nombre"}"?`,
      message:
        "Los días abiertos que no tengan horario personalizado dejarán de tener franjas horarias en este turno. Recuerda guardar la configuración para aplicar el cambio.",
      confirmText: "Sí, eliminar",
      danger: true,
    });
    if (!confirmed) return;
    setShifts((prev) => prev.filter((_, i) => i !== index));
  };

  const updateOpeningHourRange = (
    jsDay: string,
    rangeIdx: number,
    field: "open" | "close",
    value: string,
  ) => {
    setOpeningHours((prev) => {
      const existing = prev[jsDay]?.ranges ?? [
        { open: "09:00", close: "22:00" },
      ];
      const updated = existing.map((r, i) =>
        i === rangeIdx ? { ...r, [field]: value } : r,
      );
      return { ...prev, [jsDay]: { ranges: updated } };
    });
  };

  const addOpeningHourRange = (jsDay: string) => {
    setOpeningHours((prev) => {
      const existing = prev[jsDay]?.ranges ?? [];
      return {
        ...prev,
        [jsDay]: { ranges: [...existing, { open: "09:00", close: "22:00" }] },
      };
    });
  };

  const removeOpeningHourRange = (jsDay: string, rangeIdx: number) => {
    setOpeningHours((prev) => {
      const existing = prev[jsDay]?.ranges ?? [];
      const updated = existing.filter((_, i) => i !== rangeIdx);
      if (updated.length === 0) {
        const next = { ...prev };
        delete next[jsDay];
        return next;
      }
      return { ...prev, [jsDay]: { ranges: updated } };
    });
  };

  const copyOpeningHoursToNextDay = (jsDay: string) => {
    const sourceRanges = openingHours[jsDay]?.ranges ?? [];
    if (sourceRanges.length === 0) return;
    const visualIdx = JS_TO_VISUAL_DAY[parseInt(jsDay)];
    const nextVisualIdx = (visualIdx + 1) % 7;
    const nextJsDay = VISUAL_TO_JS_DAY[nextVisualIdx].toString();
    setOpeningHours((prev) => ({
      ...prev,
      [nextJsDay]: { ranges: sourceRanges.map((r) => ({ ...r })) },
    }));
  };

  const monthYear = currentDate.toLocaleDateString("es-ES", {
    month: "long",
    year: "numeric",
  });

  const selectedDateInfo = selectedDate
    ? {
        date: new Date(selectedDate),
        dayName:
          FULL_DAY_NAMES[JS_TO_VISUAL_DAY[new Date(selectedDate).getDay()]],
        formatted: new Date(selectedDate).toLocaleDateString("es-ES", {
          day: "numeric",
          month: "long",
          year: "numeric",
        }),
        override: getOverrideForDate(selectedDate),
        isDefaultClosed: !isDayOpenByDefault(new Date(selectedDate)),
      }
    : null;

  // Si TODOS los salones quedan deshabilitados en el panel, equivale a cerrar
  // el día para reservas online — se avisa en la sección "Salones". Pero esto
  // solo es cierto en modo asignación de mesa: en modo aforo
  // (`useTableAssignment === false`) los salones no limitan las reservas
  // online, solo agrupan mesas visualmente en el widget.
  const allRoomsDisabledInPanel =
    rooms.length > 0 && dayPanelDisabledRoomIds.length === rooms.length;
  const tableAssignmentModeEnabled = config?.useTableAssignment !== false;

  // Día marcado "Abierto" pero con TODOS los turnos deshabilitados: equivale
  // a cerrarlo de facto (no queda ninguna franja horaria para reservar),
  // independientemente del modo de aforo/asignación de mesa.
  const allShiftsDisabledInPanel =
    !dayPanelClosed &&
    dayPanelShifts.length > 0 &&
    dayPanelShifts.every((s) => !s.enabled);

  // Guard de ruta: en modo citas, /schedule no aplica (conceptos de
  // restaurante). El sidebar ya evita mostrar el enlace, esto cubre el
  // acceso directo por URL.
  if (currentBookingMode === "appointments") {
    return <Navigate to="/appointments/settings" replace />;
  }

  if (configLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Configuración de Horarios
          </h1>
          <p className="text-gray-500 mt-1 text-sm md:text-base">
            Gestiona los turnos de comidas y los días de apertura
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setActiveTab("calendar")}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === "calendar" ? "border-primary-500 text-primary-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}
          >
            <Calendar className="w-4 h-4 inline-block mr-2" />
            Calendario
          </button>
          <button
            onClick={() => setActiveTab("general")}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === "general" ? "border-primary-500 text-primary-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}
          >
            <Settings className="w-4 h-4 inline-block mr-2" />
            Configuración General
          </button>
        </nav>
      </div>

      {activeTab === "calendar" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() =>
                  setCurrentDate(
                    new Date(
                      currentDate.getFullYear(),
                      currentDate.getMonth() - 1,
                    ),
                  )
                }
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-lg font-semibold capitalize">{monthYear}</h2>
              <button
                onClick={() =>
                  setCurrentDate(
                    new Date(
                      currentDate.getFullYear(),
                      currentDate.getMonth() + 1,
                    ),
                  )
                }
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
              <button
                type="button"
                onClick={toggleMultiSelectMode}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${multiSelectMode ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              >
                <CheckSquare className="w-3.5 h-3.5" />
                {multiSelectMode
                  ? "Selección múltiple activa"
                  : "Seleccionar varios días"}
              </button>
              {multiSelectMode && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-primary-700 bg-primary-50 px-2 py-1 rounded-full">
                    {selectedDates.length} día
                    {selectedDates.length !== 1 ? "s" : ""} seleccionado
                    {selectedDates.length !== 1 ? "s" : ""}
                  </span>
                  <button
                    type="button"
                    onClick={toggleMultiSelectMode}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {DAY_NAMES.map((day) => (
                <div
                  key={day}
                  className="p-2 text-center text-xs font-semibold text-gray-500"
                >
                  {day}
                </div>
              ))}
              {getCalendarDays().map(({ date, isCurrentMonth }, idx) => {
                const dateKey = formatDateKey(date);
                const isSelected = !multiSelectMode && selectedDate === dateKey;
                const isMultiSelected =
                  multiSelectMode && selectedDates.includes(dateKey);
                const isClosed = isDayClosed(date);
                const isCustom = hasCustomShifts(date);
                const isToday = formatDateKey(new Date()) === dateKey;
                const override = getOverrideForDate(dateKey);
                const hasDisabledRooms = !!override?.disabledRoomIds?.length;

                return (
                  <button
                    key={idx}
                    onClick={() => {
                      if (!isCurrentMonth) return;
                      if (multiSelectMode) {
                        toggleDateSelection(dateKey);
                      } else {
                        setSelectedDate(dateKey);
                      }
                    }}
                    disabled={!isCurrentMonth}
                    className={`relative p-3 text-sm rounded-lg transition-all ${!isCurrentMonth ? "opacity-30 cursor-not-allowed" : "cursor-pointer hover:ring-2 hover:ring-primary-300"} ${isSelected ? "ring-2 ring-primary-500 ring-offset-2" : ""} ${isMultiSelected ? "ring-2 ring-blue-500 ring-offset-2" : ""} ${isClosed ? "bg-red-100 text-red-800" : isCustom ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"} ${isToday ? "font-bold" : ""}`}
                    title={
                      isClosed
                        ? `Cerrado${override?.reason ? `: ${override.reason}` : ""}`
                        : isCustom
                          ? "Horario especial"
                          : "Abierto"
                    }
                  >
                    {date.getDate()}
                    {isToday && (
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-current rounded-full" />
                    )}
                    {override && (
                      <div
                        className="absolute top-1 right-1 w-2 h-2 bg-yellow-400 rounded-full"
                        title="Tiene configuración especial"
                      />
                    )}
                    {hasDisabledRooms && (
                      <div
                        className="absolute bottom-1 right-1 w-2 h-2 bg-purple-500 rounded-full"
                        title="Tiene salones deshabilitados"
                      />
                    )}
                    {isMultiSelected && (
                      <div className="absolute -top-1.5 -left-1.5 w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {overridesLoading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4 mt-6 pt-4 border-t text-xs">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-green-100 rounded" />
                <span className="text-gray-600">Abierto</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-red-100 rounded" />
                <span className="text-gray-600">Cerrado</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-blue-100 rounded" />
                <span className="text-gray-600">Horario especial</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-yellow-400 rounded-full" />
                <span className="text-gray-600">
                  Configuración personalizada
                </span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-purple-500 rounded-full" />
                <span className="text-gray-600">Salones limitados</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            {(multiSelectMode && selectedDates.length > 0) ||
            (!multiSelectMode && selectedDate && selectedDateInfo) ? (
              <div className="bg-white rounded-lg shadow p-6 sticky top-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {multiSelectMode
                        ? `${selectedDates.length} día${selectedDates.length !== 1 ? "s" : ""} seleccionado${selectedDates.length !== 1 ? "s" : ""}`
                        : selectedDateInfo!.dayName}
                    </h3>
                    <p className="text-sm text-gray-500 capitalize">
                      {multiSelectMode
                        ? "Se aplicará la misma configuración a todos"
                        : selectedDateInfo!.formatted}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      multiSelectMode
                        ? setSelectedDates([])
                        : setSelectedDate(null)
                    }
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                <div
                  className={`p-3 rounded-lg mb-4 ${multiSelectMode || selectedDateInfo!.override ? "bg-yellow-50 border border-yellow-200" : "bg-gray-50 border border-gray-200"}`}
                >
                  <div className="flex items-center gap-2 text-sm">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    {multiSelectMode ? (
                      <span className="text-yellow-700">
                        Se sobrescribirá la configuración que hubiera en cada
                        día seleccionado con lo que definas aquí.
                      </span>
                    ) : selectedDateInfo!.override ? (
                      <span className="text-yellow-700">
                        Este día tiene configuración personalizada
                      </span>
                    ) : (
                      <span className="text-gray-600">
                        Usando configuración general (
                        {selectedDateInfo!.isDefaultClosed
                          ? "cerrado por defecto"
                          : "abierto por defecto"}
                        )
                      </span>
                    )}
                  </div>
                </div>

                <div className="mb-6">
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Estado del día
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setDayPanelClosed(false);
                        setDayPanelHasChanges(true);
                      }}
                      disabled={!canEditSchedule}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${!dayPanelClosed ? "bg-green-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                    >
                      <CalendarCheck className="w-4 h-4" />
                      Abierto
                    </button>
                    <button
                      onClick={() => {
                        setDayPanelClosed(true);
                        setDayPanelHasChanges(true);
                      }}
                      disabled={!canEditSchedule}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${dayPanelClosed ? "bg-red-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                    >
                      <CalendarX className="w-4 h-4" />
                      Cerrado
                    </button>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Motivo {!dayPanelClosed && "(opcional)"}
                  </label>
                  <input
                    type="text"
                    value={dayPanelReason}
                    disabled={!canEditSchedule}
                    onChange={(e) => {
                      setDayPanelReason(e.target.value);
                      setDayPanelHasChanges(true);
                    }}
                    placeholder="Ej: Vacaciones, evento privado..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-400"
                  />
                </div>

                {!dayPanelClosed && (
                  <div className="mb-6">
                    <label className="text-sm font-medium text-gray-700 block mb-3">
                      Horarios de este día
                    </label>
                    <div className="space-y-3">
                      {dayPanelShifts.map((shift, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">
                              {shift.name}
                            </span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={shift.enabled}
                                disabled={!canEditSchedule}
                                onChange={(e) =>
                                  updateDayPanelShift(
                                    idx,
                                    "enabled",
                                    e.target.checked,
                                  )
                                }
                                className="sr-only peer"
                              />
                              <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-primary-500 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-disabled:opacity-50"></div>
                            </label>
                          </div>
                          {shift.enabled && (
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-gray-500">
                                  Inicio
                                </label>
                                <input
                                  type="time"
                                  value={shift.startTime}
                                  disabled={!canEditSchedule}
                                  onChange={(e) =>
                                    updateDayPanelShift(
                                      idx,
                                      "startTime",
                                      e.target.value,
                                    )
                                  }
                                  className="w-full px-2 py-1 border rounded text-sm disabled:bg-gray-100 disabled:text-gray-400"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-gray-500">
                                  Fin
                                </label>
                                <input
                                  type="time"
                                  value={shift.endTime}
                                  disabled={!canEditSchedule}
                                  onChange={(e) =>
                                    updateDayPanelShift(
                                      idx,
                                      "endTime",
                                      e.target.value,
                                    )
                                  }
                                  className="w-full px-2 py-1 border rounded text-sm disabled:bg-gray-100 disabled:text-gray-400"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {allShiftsDisabledInPanel && (
                      <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-start gap-2">
                        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>
                          Has marcado el día como abierto pero todos los
                          turnos están deshabilitados: no habrá ninguna franja
                          disponible, equivale a cerrar{" "}
                          {multiSelectMode ? "estos días" : "el día"} para
                          reservas.
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {!dayPanelClosed && rooms.length > 0 && (
                  <div className="mb-6">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-2">
                      <DoorOpen className="w-4 h-4 text-gray-400" />
                      Salones
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Desactiva los salones que no estarán disponibles para
                      reservas online{" "}
                      {multiSelectMode ? "estos días" : "este día"} (ej: solo
                      abrir un salón por un evento).
                    </p>
                    <div className="space-y-2">
                      {rooms.map((room) => {
                        const isRoomEnabled =
                          !dayPanelDisabledRoomIds.includes(room.id);
                        return (
                          <div
                            key={room.id}
                            className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg"
                          >
                            <span className="text-sm text-gray-700">
                              {room.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => toggleDayPanelRoom(room.id)}
                              disabled={!canEditSchedule}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 disabled:cursor-not-allowed disabled:opacity-60 ${isRoomEnabled ? "bg-primary-500" : "bg-gray-300"}`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isRoomEnabled ? "translate-x-4" : "translate-x-0.5"}`}
                              />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    {allRoomsDisabledInPanel &&
                      (tableAssignmentModeEnabled ? (
                        <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-start gap-2">
                          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>
                            Has deshabilitado todos los salones: equivale a
                            cerrar {multiSelectMode ? "estos días" : "el día"}{" "}
                            para reservas online.
                          </span>
                        </div>
                      ) : (
                        <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 flex items-start gap-2">
                          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>
                            Esta ubicación funciona en modo aforo: deshabilitar
                            salones solo los oculta del widget de reservas
                            online, no limita el aforo ni bloquea reservas.
                          </span>
                        </div>
                      ))}
                  </div>
                )}

                {!canEditSchedule && (
                  <div className="mb-3 p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-500 flex items-start gap-2">
                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      Solo owner/admin pueden editar el horario. Puedes
                      consultarlo, pero no guardar cambios.
                    </span>
                  </div>
                )}
                <div className="space-y-2">
                  <button
                    onClick={multiSelectMode ? saveMultiDayOverride : saveDayOverride}
                    disabled={
                      !canEditSchedule ||
                      dayPanelSaving ||
                      !dayPanelHasChanges ||
                      (multiSelectMode && selectedDates.length === 0)
                    }
                    className="w-full py-2 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {dayPanelSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Guardar cambios
                  </button>
                  {(multiSelectMode || selectedDateInfo!.override) && (
                    <button
                      onClick={
                        multiSelectMode
                          ? resetMultiDaysToDefault
                          : resetDayToDefault
                      }
                      disabled={!canEditSchedule || dayPanelSaving}
                      className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      Restaurar configuración por defecto
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-center text-gray-500 py-8">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">
                    {multiSelectMode
                      ? "Selecciona varios días"
                      : "Selecciona un día"}
                  </p>
                  <p className="text-sm mt-1">
                    {multiSelectMode
                      ? "Haz clic en los días del calendario que quieras editar juntos"
                      : "Haz clic en un día del calendario para ver y editar su horario"}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "general" && (
        <div className="space-y-6">
          {/* Turnos por defecto */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary-600" />
                <h2 className="text-base font-semibold text-gray-900">
                  Turnos por defecto
                </h2>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Estos turnos se aplicarán a todos los días abiertos que no
                tengan configuración personalizada
              </p>
            </div>
            <div className="p-5 space-y-3">
              {shifts.map((shift, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <input
                      type="text"
                      value={shift.name}
                      disabled={!canEditSchedule}
                      onChange={(e) => updateShift(idx, "name", e.target.value)}
                      className="font-medium bg-transparent border-b border-transparent hover:border-gray-300 focus:border-primary-500 focus:outline-none text-sm disabled:text-gray-400"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          updateShift(idx, "enabled", !shift.enabled)
                        }
                        disabled={!canEditSchedule}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${shift.enabled ? "bg-primary-500" : "bg-gray-200"}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${shift.enabled ? "translate-x-4" : "translate-x-0.5"}`}
                        />
                      </button>
                      {shifts.length > 1 && (
                        <button
                          onClick={() => removeShift(idx)}
                          disabled={!canEditSchedule}
                          className="p-1 text-red-500 hover:bg-red-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">Inicio</label>
                      <input
                        type="time"
                        value={shift.startTime}
                        onChange={(e) =>
                          updateShift(idx, "startTime", e.target.value)
                        }
                        className="w-full px-2 py-1 border rounded text-sm disabled:bg-gray-100 disabled:text-gray-400"
                        disabled={!shift.enabled || !canEditSchedule}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Fin</label>
                      <input
                        type="time"
                        value={shift.endTime}
                        onChange={(e) =>
                          updateShift(idx, "endTime", e.target.value)
                        }
                        className="w-full px-2 py-1 border rounded text-sm disabled:bg-gray-100 disabled:text-gray-400"
                        disabled={!shift.enabled || !canEditSchedule}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                onClick={addShift}
                disabled={!canEditSchedule}
                className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-400 hover:text-primary-600 flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-gray-500"
              >
                <Plus className="w-4 h-4" />
                Añadir turno
              </button>
            </div>
          </div>

          {/* Días de apertura por defecto */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary-600" />
                <h2 className="text-base font-semibold text-gray-900">
                  Días de apertura por defecto
                </h2>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Selecciona qué días de la semana está abierto normalmente el
                restaurante
              </p>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-7 gap-2">
                {DAY_NAMES.map((day, visualIdx) => {
                  const jsDay = VISUAL_TO_JS_DAY[visualIdx];
                  const isOpen = weeklySchedule[jsDay.toString()] !== false;
                  return (
                    <button
                      key={day}
                      onClick={() => toggleDefaultDay(visualIdx)}
                      disabled={!canEditSchedule}
                      className={`py-3 rounded-lg text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${isOpen ? "bg-green-100 text-green-800 hover:bg-green-200" : "bg-red-100 text-red-800 hover:bg-red-200"}`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Horario de apertura del local */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setOpeningHoursCollapsed((v) => !v)}
              className="w-full flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50 text-left hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary-600" />
                <div>
                  <h2 className="text-base font-semibold text-gray-900">
                    Horario de apertura del local
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Cuándo está físicamente abierto el negocio (se muestra a los
                    clientes)
                  </p>
                </div>
              </div>
              {openingHoursCollapsed ? (
                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronLeft className="w-5 h-5 text-gray-400 flex-shrink-0 rotate-90" />
              )}
            </button>
            {!openingHoursCollapsed && (
              <div className="p-5">
                <p className="text-sm text-gray-500 mb-4">
                  Indica cuándo está físicamente abierto el negocio. Puedes
                  añadir varias franjas por día (ej: 9:00–14:00 y 17:00–23:00).
                </p>
                <div className="space-y-3">
                  {VISUAL_TO_JS_DAY.map((jsDay, visualIdx) => {
                    const dayKey = jsDay.toString();
                    const isOpen = weeklySchedule[dayKey] !== false;
                    const ranges = openingHours[dayKey]?.ranges ?? [];
                    return (
                      <div key={jsDay} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-24 text-sm font-semibold text-gray-700 shrink-0">
                            {FULL_DAY_NAMES[visualIdx]}
                          </span>
                          {!isOpen && (
                            <span className="text-sm text-gray-400 italic">
                              Cerrado
                            </span>
                          )}
                          {isOpen && ranges.length === 0 && (
                            <button
                              type="button"
                              onClick={() => addOpeningHourRange(dayKey)}
                              disabled={!canEditSchedule}
                              className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Plus className="w-3.5 h-3.5" /> Añadir franja
                              horaria
                            </button>
                          )}
                        </div>
                        {isOpen && ranges.length > 0 && (
                          <div className="space-y-2">
                            {ranges.map((range, rIdx) => (
                              <div
                                key={rIdx}
                                className="flex items-center gap-2 flex-wrap"
                              >
                                <input
                                  type="time"
                                  value={range.open}
                                  disabled={!canEditSchedule}
                                  onChange={(e) =>
                                    updateOpeningHourRange(
                                      dayKey,
                                      rIdx,
                                      "open",
                                      e.target.value,
                                    )
                                  }
                                  className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-400"
                                />
                                <span className="text-gray-400 text-sm">—</span>
                                <input
                                  type="time"
                                  value={range.close}
                                  disabled={!canEditSchedule}
                                  onChange={(e) =>
                                    updateOpeningHourRange(
                                      dayKey,
                                      rIdx,
                                      "close",
                                      e.target.value,
                                    )
                                  }
                                  className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-400"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeOpeningHourRange(dayKey, rIdx)
                                  }
                                  disabled={!canEditSchedule}
                                  className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                  title="Eliminar franja"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                                {rIdx === ranges.length - 1 && (
                                  <button
                                    type="button"
                                    onClick={() => addOpeningHourRange(dayKey)}
                                    disabled={!canEditSchedule}
                                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium ml-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <Plus className="w-3.5 h-3.5" /> Añadir
                                    franja
                                  </button>
                                )}
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => copyOpeningHoursToNextDay(dayKey)}
                              disabled={!canEditSchedule}
                              className="flex items-center gap-1 text-xs text-gray-500 hover:text-primary-600 font-medium mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Copiar este horario al día siguiente"
                            >
                              <Copy className="w-3.5 h-3.5" /> Copiar al día
                              siguiente
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Zona horaria */}
          {/* Sin overflow-hidden: recortaba el desplegable del buscador de
              zonas; el redondeo superior lo aporta la propia cabecera. */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 rounded-t-xl">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary-600" />
                <h2 className="text-base font-semibold text-gray-900">
                  Zona horaria
                </h2>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Las horas de los turnos y reservas se interpretarán en esta zona
                horaria
              </p>
            </div>
            <div className="p-5">
              <div className="w-full sm:w-72">
                <TimezoneSelect
                  value={timezone}
                  onChange={setTimezone}
                  disabled={!canEditSchedule}
                />
              </div>
            </div>
          </div>

          {!canEditSchedule && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 flex items-start gap-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Solo owner/admin pueden editar el horario. Puedes consultar la
                configuración, pero no guardar cambios.
              </span>
            </div>
          )}

          {/* Botón guardar */}
          <div className="flex justify-end">
            <button
              onClick={saveGeneralConfig}
              disabled={configSaving || !canEditSchedule}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors text-sm font-medium"
            >
              {configSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {configSaving ? "Guardando..." : "Guardar configuración"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
